/**
 * Seção do assessment SafePlace oferecido como cortesia.
 *
 * Existe porque o sinalizador de assessment era gravado nas premissas e **nenhum renderizador o
 * consumia**: o vendedor marcava a opção no formulário e a proposta saía idêntica. Era o defeito
 * relatado como "nada acontece", e a regra de negócio já previa o comportamento correto — a tabela
 * de itens sem preço em `regras-negocio.md` registra "gera seção de conteúdo".
 *
 * O que a seção precisa deixar claro, na ordem: que é cortesia, qual pilar do Well-Architected
 * Framework será avaliado, o que exatamente será analisado e o que o cliente recebe no fim. Sem o
 * escopo declarado, a cortesia vira promessa vaga que o time de entrega herda sem contorno.
 */

import {
  FOCO_ASSESSMENT_CURTO,
  PILAR_ASSESSMENT,
  type DocumentoProposta,
  type FocoAssessment,
} from '@emaster/dominio';

import { cabecalhoSecao } from './componentes.ts';
import { escaparHtml } from './escapar.ts';
import { CONTAINER, icone } from './tokens.ts';

interface ConteudoAssessment {
  /** Frase de abertura: o problema que a avaliação resolve, na linguagem do cliente. */
  readonly chamada: string;
  readonly iconeNome: string;
  /** O que é examinado no ambiente. */
  readonly analisado: readonly { readonly iconeNome: string; readonly texto: string }[];
  /** O que o cliente recebe ao final. */
  readonly entregaveis: readonly string[];
}

const CONTEUDO: Readonly<Record<FocoAssessment, ConteudoAssessment>> = {
  custos: {
    chamada:
      'Conta AWS cresce por acúmulo: recurso provisionado para um teste que ninguém desligou, volume desanexado que continua sendo cobrado, instância grande demais para a carga que realmente roda. A avaliação encontra esse desperdício e o quantifica.',
    iconeNome: 'trending-down',
    analisado: [
      {
        iconeNome: 'maximize-2',
        texto:
          'Dimensionamento de instâncias de computação e de banco, comparando a capacidade contratada com o uso medido.',
      },
      {
        iconeNome: 'unplug',
        texto:
          'Recursos órfãos: volumes desanexados, endereços IP ociosos, snapshots antigos e ambientes esquecidos.',
      },
      {
        iconeNome: 'badge-percent',
        texto:
          'Cobertura de Savings Plans e Instâncias Reservadas frente ao perfil de consumo estável.',
      },
      {
        iconeNome: 'archive',
        texto:
          'Classes de armazenamento e políticas de ciclo de vida: dado frio pagando preço de dado quente.',
      },
      {
        iconeNome: 'arrow-left-right',
        texto:
          'Transferência de dados entre zonas e regiões, uma das linhas menos observadas da fatura.',
      },
      {
        iconeNome: 'tags',
        texto:
          'Marcação de recursos, orçamentos e alertas de anomalia para rateio por área ou projeto.',
      },
    ],
    entregaveis: [
      'Relatório com os achados priorizados pela economia estimada de cada um',
      'Plano de ação declarando esforço e risco por recomendação, para você decidir o que entra primeiro',
      'Sessão de apresentação dos resultados com o time técnico e o time financeiro',
    ],
  },

  seguranca: {
    chamada:
      'A maioria dos incidentes em nuvem não vem de ataque sofisticado: vem de permissão ampla demais concedida às pressas, de porta aberta que ninguém fechou e de backup que nunca foi testado. A avaliação procura essas aberturas antes que alguém as encontre.',
    iconeNome: 'shield-check',
    analisado: [
      {
        iconeNome: 'key-round',
        texto:
          'Identidade e acesso: privilégio mínimo em IAM, uso de MFA e chaves de longa duração em circulação.',
      },
      {
        iconeNome: 'lock',
        texto: 'Criptografia em repouso e em trânsito, e a gestão das chaves que a sustenta.',
      },
      {
        iconeNome: 'globe',
        texto:
          'Exposição de rede: grupos de segurança permissivos, armazenamento público e endpoints abertos à internet.',
      },
      {
        iconeNome: 'scroll-text',
        texto:
          'Registro e auditoria: trilha de eventos habilitada, retida e efetivamente monitorada.',
      },
      {
        iconeNome: 'database-backup',
        texto:
          'Backup e recuperação: cobertura real dos dados críticos e evidência de teste de restauração.',
      },
      {
        iconeNome: 'scale',
        texto: 'Aderência à LGPD na arquitetura, e não apenas no contrato.',
      },
    ],
    entregaveis: [
      'Relatório com os riscos classificados por severidade e por facilidade de exploração',
      'Plano de remediação priorizado, separando o que se corrige em horas do que exige projeto',
      'Sessão de apresentação dos resultados com o time técnico e a liderança',
    ],
  },
};

/** Bloco de um foco: pílula do pilar, o que é analisado e o que o cliente recebe. */
function blocoFoco(foco: FocoAssessment): string {
  const conteudo = CONTEUDO[foco];

  const analisado = conteudo.analisado
    .map(
      (a) => `<li class="flex items-start gap-3">
                                <span class="mt-0.5 shrink-0 text-brand-accent">${icone(a.iconeNome, 'w-5 h-5')}</span>
                                <span class="text-sm leading-relaxed text-gray-600">${escaparHtml(a.texto)}</span>
                            </li>`,
    )
    .join('\n                            ');

  const entregaveis = conteudo.entregaveis
    .map(
      (e) => `<li class="flex items-start gap-3">
                                <span class="mt-0.5 shrink-0 text-brand-accent">${icone('check', 'w-5 h-5')}</span>
                                <span class="text-sm leading-relaxed text-white/90">${escaparHtml(e)}</span>
                            </li>`,
    )
    .join('\n                            ');

  return `
            <div class="mb-10 last:mb-0">
                <div class="mb-6 max-w-3xl">
                    <span class="inline-flex items-center gap-2 rounded-full bg-gray-100 px-4 py-2 text-sm font-semibold text-brand-blue">
                        ${icone(conteudo.iconeNome, 'w-4 h-4')} Pilar ${escaparHtml(PILAR_ASSESSMENT[foco])}
                    </span>
                    <h3 class="mt-4 text-2xl font-bold text-brand-blue">${escaparHtml(FOCO_ASSESSMENT_CURTO[foco])}</h3>
                    <p class="mt-3 leading-relaxed text-gray-600">${escaparHtml(conteudo.chamada)}</p>
                </div>

                <div class="grid gap-8 lg:grid-cols-5">
                    <div class="lg:col-span-3">
                        <div class="rounded-3xl border border-gray-100 bg-gray-50 p-8">
                            <h4 class="mb-5 text-base font-bold text-brand-blue">O que será analisado</h4>
                            <ul class="space-y-4">
                                ${analisado}
                            </ul>
                        </div>
                    </div>

                    <div class="lg:col-span-2">
                        <div class="rounded-3xl bg-brand-blue p-8">
                            <h4 class="mb-5 text-base font-bold text-white">O que você recebe</h4>
                            <ul class="space-y-4">
                                ${entregaveis}
                            </ul>
                            <p class="mt-6 border-t border-white/15 pt-4 text-xs leading-relaxed text-white/70">
                                A execução é agendada com o seu time após a aprovação desta proposta. O relatório é seu, independente de qualquer contratação adicional.
                            </p>
                        </div>
                    </div>
                </div>
            </div>`;
}

/**
 * Renderiza a seção do assessment.
 *
 * Percorre os focos, então o mesmo código serve para um ou para dois. A lista chega ordenada pelo
 * formulário, e a ordem é preservada: é a ordem em que o vendedor apresentou ao cliente.
 *
 * @returns HTML da seção, ou string vazia quando a proposta não inclui assessment.
 */
export function secaoAssessment(doc: DocumentoProposta): string {
  const focos = doc.premissas.assessment;
  if (focos === null || focos.length === 0) return '';

  const titulo = `Assessment SafePlace — ${focos.map((f) => FOCO_ASSESSMENT_CURTO[f]).join(' e ')}`;

  return `
    <section id="assessment" class="scroll-mt-24 border-t border-gray-200 bg-white py-20">
        <div class="${CONTAINER}">
            ${cabecalhoSecao('Cortesia', titulo, [
              focos.length > 1
                ? 'Duas avaliações do seu ambiente, sem custo: uma olhando para onde o dinheiro está sendo desperdiçado, outra para onde a operação está exposta.'
                : 'Uma avaliação do seu ambiente, sem custo, conduzida por arquitetos certificados da eMaster.',
              'As avaliações seguem o AWS Well-Architected Framework, o mesmo referencial que a AWS usa para revisar arquiteturas.',
            ])}

            <div class="mb-8">
                <span class="inline-flex items-center gap-2 rounded-full bg-brand-accent/10 px-4 py-2 text-sm font-bold text-brand-accent">
                    ${icone('gift', 'w-4 h-4')} Incluído sem custo nesta proposta
                </span>
            </div>
${focos.map(blocoFoco).join('\n')}
        </div>
    </section>`;
}
